import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getComplianceEntities, previewComplianceExport, complianceExportUrl } from '../lib/api';
import { FileZip, Download, Calendar, Checks, Warning, Package } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function Compliance() {
  const [entities, setEntities] = useState([]);
  const [selected, setSelected] = useState([]);
  const [startDate, setStartDate] = useState('2025-01-01');
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [includeArchive, setIncludeArchive] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getComplianceEntities().then(res => {
      setEntities(res.data);
      setSelected(res.data.map(e => e.entity)); // default: select all
    }).catch(() => {});
  }, []);

  const toggleEntity = (e) => {
    setSelected(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await previewComplianceExport({
        start_date: startDate,
        end_date: endDate,
        entities: selected,
        include_archive: includeArchive,
      });
      setPreview(res.data);
    } catch (e) {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleExport = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one entity');
      return;
    }
    setExporting(true);
    try {
      const res = await fetch(complianceExportUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          entities: selected,
          include_archive: includeArchive,
        }),
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stockaris-compliance-${startDate}-${endDate}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export downloaded (${res.headers.get('X-Total-Records') || '?'} records)`);
    } catch (e) {
      toast.error('Export failed: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div data-testid="compliance-page" className="p-6 max-w-5xl">
        <TenantExportSection />
        <UptimeWidget />
        <div className="mb-6 mt-6">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <FileZip size={32} weight="fill" className="text-brand" />
            Compliance Export
          </h1>
          <p className="text-muted-foreground">Download a signed ZIP of records + manifest for auditors</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 mb-4 space-y-5">
          {/* Date range */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="overline">Start Date</Label>
              <div className="relative mt-1">
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="pl-9 font-mono"
                  data-testid="compliance-start-date"
                />
              </div>
            </div>
            <div>
              <Label className="overline">End Date</Label>
              <div className="relative mt-1">
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="pl-9 font-mono"
                  data-testid="compliance-end-date"
                />
              </div>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setIncludeArchive(!includeArchive)}
                data-testid="compliance-include-archive"
                className={`w-full h-10 px-3 border text-sm font-bold uppercase tracking-widest ${includeArchive ? 'bg-brand text-white border-brand' : 'bg-card border-border rounded-lg text-muted-foreground'}`}
              >
                {includeArchive ? '✓ Include Archive' : 'Active Only'}
              </button>
            </div>
          </div>

          {/* Entity checkboxes */}
          <div>
            <Label className="overline mb-2 block">Entities ({selected.length}/{entities.length})</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {entities.map(e => (
                <button
                  key={e.entity}
                  onClick={() => toggleEntity(e.entity)}
                  data-testid={`compliance-entity-${e.entity}`}
                  className={`border px-3 py-2 text-sm text-left flex items-center gap-2 ${selected.includes(e.entity) ? 'bg-blue-50 border-brand text-brand' : 'bg-card border-border rounded-lg hover:bg-muted'}`}
                >
                  {selected.includes(e.entity) ? <Checks size={14} weight="bold" /> : <Package size={14} />}
                  <span className="font-mono">{e.entity}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setSelected(entities.map(e => e.entity))}
                className="text-xs text-brand font-bold uppercase"
              >
                Select all
              </button>
              <button
                onClick={() => setSelected([])}
                className="text-xs text-muted-foreground font-bold uppercase"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={previewing || selected.length === 0}
              data-testid="compliance-preview-btn"
            >
              {previewing ? 'Previewing...' : 'Preview'}
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting || selected.length === 0}
              className="bg-brand hover:bg-brand/90"
              data-testid="compliance-export-btn"
            >
              <Download size={18} className="mr-2" />
              {exporting ? 'Generating ZIP...' : 'Download ZIP'}
            </Button>
          </div>
        </div>

        {/* Preview results */}
        {preview && (
          <div className="bg-card border border-border rounded-lg" data-testid="compliance-preview-result">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <p className="font-bold text-lg">
                  Preview · {preview.total_records.toLocaleString()} total records
                </p>
                <p className="text-sm text-muted-foreground">
                  {new Date(preview.start_date).toLocaleDateString()} – {new Date(preview.end_date).toLocaleDateString()} · {preview.include_archive ? 'Active + Archive' : 'Active only'}
                </p>
              </div>
              {preview.total_records === 0 && (
                <span className="text-amber-700 text-sm font-bold uppercase flex items-center gap-1">
                  <Warning size={14} weight="bold" /> No records in range
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
            <table className="w-full table-dense">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Active</th>
                  <th>Archived</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {preview.entities.map(e => (
                  <tr key={e.entity} data-testid={`preview-row-${e.entity}`}>
                    <td className="font-mono font-bold">{e.entity}</td>
                    <td className="font-mono">{e.active_count.toLocaleString()}</td>
                    <td className="font-mono text-muted-foreground">{e.archive_count.toLocaleString()}</td>
                    <td className="font-mono font-bold text-brand">{e.total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

// ============================================================================
// Phase D — Tenant Data Export (GDPR Art. 20 / APP 12)
// ============================================================================
import axios from 'axios';
import { Shield, CheckCircle, XCircle } from '@phosphor-icons/react';
const API = process.env.REACT_APP_BACKEND_URL;

function TenantExportSection() {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/compliance/export-company-data`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename=([^;]+)/);
      a.download = (match ? match[1].trim() : `stockaris-export-${new Date().toISOString().slice(0,10)}.zip`);
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Tenant data export downloaded');
    } catch (e) {
      toast.error('Export failed: ' + (e.message || ''));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="tenant-export-section" className="bg-card border border-border rounded-lg p-5 mb-4">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-brand flex items-center justify-center shrink-0">
          <Shield size={20} weight="fill" className="text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-bold text-lg">Tenant Data Export</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Download a complete ZIP of YOUR company's data (GDPR Art. 20 / Australian APP 12). One JSON file per collection + manifest.
          </p>
        </div>
        <Button
          data-testid="tenant-export-btn"
          onClick={download}
          disabled={busy}
          className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold"
        >
          <Download size={14} weight="bold" className="mr-2" />
          {busy ? 'Preparing…' : 'Download ZIP'}
        </Button>
      </div>
    </div>
  );
}

function UptimeWidget() {
  const [u, setU] = React.useState(null);
  React.useEffect(() => {
    axios.get(`${API}/api/compliance/uptime`, { withCredentials: true })
      .then((r) => setU(r.data))
      .catch(() => {});
  }, []);
  if (!u) return null;
  const color = u.status === 'operational' ? 'text-emerald-600' : u.status === 'degraded' ? 'text-amber-600' : 'text-rose-600';
  const Icon = u.status === 'operational' ? CheckCircle : u.status === 'degraded' ? Warning : XCircle;
  return (
    <div data-testid="uptime-widget" className="bg-card border border-border rounded-lg p-5 mb-4 flex items-center gap-4">
      <Icon size={28} weight="fill" className={color} />
      <div className="flex-1">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">System status</div>
        <div className={`text-lg font-bold ${color}`}>
          {u.status === 'operational' ? 'All systems operational' : u.status === 'degraded' ? 'Degraded performance' : 'Outage'}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 font-mono">
          {u.uptime_pct_30d}% · last 30 days · {u.incidents_30d} incidents
        </div>
      </div>
    </div>
  );
}

